import { useEffect, useRef, useState } from "react";
import axiosInstance from "./axiosInstance";
import "./StreamingPage.css";

function parseChannelNumbers() {
  try {
    const parsed = JSON.parse(process.env.REACT_APP_CONFIG_CHANNEL_NUMBER || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    console.error("Invalid REACT_APP_CONFIG_CHANNEL_NUMBER", e);
    return [];
  }
}

function getChannelFromUrl() {
  const ch = parseInt(new URLSearchParams(window.location.search).get("channel"), 10);
  return Number.isNaN(ch) ? null : ch;
}

const configChannelNumbers = parseChannelNumbers();
const mediaPlayerUrl = process.env.REACT_APP_MEDIA_PLAYER_URL || "";
const mediaChannelPrefix = process.env.REACT_APP_MEDIA_CHANNEL_PREFIX || "";
const tpUrlTemplate = process.env.REACT_APP_TP_URL || "";
const fetchClientIp = process.env.REACT_APP_FETCH_CLIENT_IP === "true";

// How often to check whether the async flashphoner.js script has loaded
const FLASHPHONER_POLL_MS = 200;
// Delay before reconnecting after the stream stops/fails (e.g. feed not live yet)
const RETRY_DELAY_MS = 5000;

export default function StreamingPage() {
  const [channelNumber] = useState(getChannelFromUrl);
  const [tpUrl, setTpUrl] = useState("");
  const [checkedDomain, setCheckedDomain] = useState(null);
  const [isApproved, setIsApproved] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [needsUnmute, setNeedsUnmute] = useState(false);
  const playerRef = useRef(null);
  const videoRef = useRef(null);

  const isFlashphonerChannel =
    channelNumber !== null && configChannelNumbers.includes(channelNumber);

  useEffect(() => {
    async function checkDomainApproval() {
      setIsLoading(true);
      try {
        let urlString = "";

        if (window.location.ancestorOrigins && window.location.ancestorOrigins.length > 0) {
          urlString = window.location.ancestorOrigins[window.location.ancestorOrigins.length - 1];
        }

        if (!urlString && document.referrer) {
          urlString = document.referrer;
        }

        if (!urlString) {
          try {
            if (window.parent !== window) {
              urlString = window.parent.location.href;
            }
          } catch (e) {
            console.warn("Cannot access parent location due to CORS", e);
          }
        }

        if (!urlString) {
          urlString = window.location.href;
        }

        if (!urlString) return;

        const url = new URL(urlString);
        let domain = url.hostname;

        setCheckedDomain(domain);

        const parts = domain.split(".");
        const siteDomain = parts.slice(-2).join(".");
        const siteName = siteDomain;
        const subDomains =
          parts.length > 2 ? parts.slice(0, parts.length - 2) : [];

        const payload = {
          siteName,
          siteDomain,
          subDomains,
        };

        const response = await axiosInstance.post(
          "/admin/domain/streamingApprove",
          payload
        );

        setIsApproved(response?.data?.result?.isVideoApproved ?? false);

      } catch (err) {
        setIsApproved(false);
      } finally {
        setIsLoading(false);
      }
    }

    checkDomainApproval();
  }, []);

  // Third-party channels: build the iframe URL once the domain is approved.
  useEffect(() => {
    if (!channelNumber || isFlashphonerChannel || isApproved !== true) return;

    let cancelled = false;
    const buildTpUrl = (ip) =>
      tpUrlTemplate.replace("{channel}", channelNumber).replace("{ip}", ip);

    if (fetchClientIp) {
      fetch("https://api.ipify.org?format=json")
        .then((res) => res.json())
        .then((data) => !cancelled && setTpUrl(buildTpUrl(data.ip)))
        .catch((err) => console.error("Failed to fetch IP:", err));
    } else {
      setTpUrl(buildTpUrl(""));
    }

    return () => {
      cancelled = true;
    };
  }, [channelNumber, isFlashphonerChannel, isApproved]);

  // Flashphoner channels: start only once the domain is approved and the
  // #player div is mounted; wait for the async script; retry on stop/failure.
  useEffect(() => {
    const display = playerRef.current;
    if (!isFlashphonerChannel || isApproved !== true || isLoading || !display) return;

    const streamName = `${mediaChannelPrefix}${channelNumber}`;
    const wssUrl = mediaPlayerUrl.replace("https://", "wss://").replace("http://", "ws://");

    let cancelled = false;
    let session = null;
    let timer = null;

    const releaseSession = () => {
      if (session) {
        const old = session;
        session = null;
        try {
          old.disconnect();
        } catch (e) {
          console.warn("Flashphoner disconnect failed", e);
        }
      }
      videoRef.current = null;
      display.replaceChildren();
      setNeedsUnmute(false);
    };

    const runLater = (fn, delay) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        fn();
      }, delay);
    };

    const scheduleRetry = (reason) => {
      if (cancelled) return;
      console.warn(`Stream ${streamName}: ${reason}, retrying in ${RETRY_DELAY_MS / 1000}s`);
      releaseSession();
      runLater(connect, RETRY_DELAY_MS);
    };

    const connect = async () => {
      if (cancelled) return;

      const Flashphoner = window.Flashphoner;
      if (!Flashphoner) {
        runLater(connect, FLASHPHONER_POLL_MS);
        return;
      }

      try {
        await Flashphoner.init({});
        if (cancelled) return;

        const { SESSION_STATUS, STREAM_STATUS } = Flashphoner.constants;
        const current = Flashphoner.createSession({ urlServer: wssUrl });
        session = current;
        // Events from a session we already released must not trigger anything
        const isStale = () => cancelled || session !== current;

        current.on(SESSION_STATUS.ESTABLISHED, (s) => {
          if (isStale()) return;

          const stream = s.createStream({
            name: streamName,
            display,
            // Browsers pause a video unmuted without a user gesture, so stay
            // muted and show the "Tap to unmute" button instead
            unmutePlayOnStart: false,
          });

          stream.on(STREAM_STATUS.PLAYING, () => {
            if (isStale()) return;
            const videoEl = display.querySelector("video");
            if (!videoEl) return;
            if (videoEl !== videoRef.current) {
              videoEl.setAttribute("controls", "true");
              videoEl.setAttribute("playsinline", "true");
              videoEl.addEventListener("volumechange", () => setNeedsUnmute(videoEl.muted));
              videoRef.current = videoEl;
            }
            setNeedsUnmute(videoEl.muted);
          });

          stream.on(STREAM_STATUS.STOPPED, () => {
            if (!isStale()) scheduleRetry("stream stopped");
          });

          stream.on(STREAM_STATUS.FAILED, (st) => {
            if (!isStale()) scheduleRetry(`stream failed (${st.getInfo?.() || "unknown"})`);
          });

          stream.play();
        });

        current.on(SESSION_STATUS.DISCONNECTED, () => {
          if (!isStale()) scheduleRetry("session disconnected");
        });

        current.on(SESSION_STATUS.FAILED, () => {
          if (!isStale()) scheduleRetry("session failed");
        });
      } catch (e) {
        scheduleRetry(`connect error (${e.message})`);
      }
    };

    connect();

    return () => {
      cancelled = true;
      clearTimeout(timer);
      releaseSession();
    };
  }, [channelNumber, isFlashphonerChannel, isApproved, isLoading]);

  const handleUnmute = () => {
    const videoEl = videoRef.current;
    if (!videoEl) return;
    videoEl.muted = false;
    videoEl.play().catch(() => {});
  };

  useEffect(() => {
    document.title = "Streaming";
  }, []);

  if (isLoading) return null;

  if (isApproved === false) {
    return (
      <div className="unauthorized-container">
        <div className="unauthorized-text">
          You are not Authorized to view this stream.&nbsp; &#128577; <br />
          Your domain is {checkedDomain || window.location.hostname}
        </div>
      </div>
    );
  }

  if (isFlashphonerChannel) {
    return (
      <div className="player-wrapper">
        <div id="player" ref={playerRef} className="video-frame" />
        {needsUnmute && (
          <button type="button" className="unmute-button" onClick={handleUnmute}>
            &#128263; Tap to unmute
          </button>
        )}
      </div>
    );
  }

  if (!tpUrl) return null;

  return (
    <iframe
      src={tpUrl}
      className="video-frame"
      frameBorder="0"
      allowFullScreen
      allow="autoplay; fullscreen; encrypted-media; picture-in-picture; web-share"
      title="Third-party Stream"
    />
  );
}
